// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton), setters (stable), and local vars. See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Package, AlertCircle, ExternalLink, Copy } from "lucide-react";
import { api } from "@/lib/api";
import StatusTimeline from "@/components/StatusTimeline";
import { toast } from "sonner";

export default function PublicClaim() {
  const { token } = useParams();
  const [claim, setClaim] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/public/claims/${token}`)
      .then(r => setClaim(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Claim not found"));
  }, [token]);

  return (
    <div className="min-h-screen bg-background grain">
      <header className="max-w-4xl mx-auto px-6 pt-8">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-secondary" /></div>
          <div>
            <div className="font-display font-semibold text-lg">Treasurescare</div>
            <div className="text-[10px] overline text-muted-foreground">Repair status</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {err && (
          <div className="p-6 bg-destructive/5 border border-destructive/20 rounded-2xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
            <div>
              <div className="font-medium">Claim not found</div>
              <div className="text-sm text-muted-foreground mt-1">This link may be invalid or has been revoked. Please double-check with the case owner.</div>
            </div>
          </div>
        )}
        {claim && (
          <>
            <div className="overline text-secondary">Live case status</div>
            <h1 className="mt-2 font-display text-4xl lg:text-5xl font-light tracking-tight">{claim.case_number}</h1>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] overline px-3 py-1 rounded-full bg-primary text-primary-foreground">{claim.status.replace("_"," ")}</span>
              <span className="text-[10px] overline px-3 py-1 rounded-full bg-muted text-muted-foreground capitalize">{claim.issue_category}</span>
              {claim.priority && <span className="text-[10px] overline px-3 py-1 rounded-full bg-secondary/20 text-secondary">{claim.priority} priority</span>}
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6">
                <div className="overline text-secondary">Repair timeline</div>
                <div className="mt-6"><StatusTimeline status={claim.status} events={claim.timeline} /></div>
              </div>
              <div className="space-y-6">
                <div className="bg-card border border-border rounded-2xl p-6">
                  <div className="overline text-secondary flex items-center gap-2"><Package className="w-3 h-3" /> Device</div>
                  <div className="mt-3 font-display text-xl">{claim.device?.brand} {claim.device?.model}</div>
                  <div className="text-xs text-muted-foreground mt-1 font-mono">IMEI {claim.device?.imei_masked || "—"}</div>
                </div>
                <div className="bg-card border border-border rounded-2xl p-6">
                  <div className="overline text-secondary">Courier</div>
                  <div className="mt-3 font-medium">{claim.courier || "—"}</div>
                  {claim.tracking_number && <div className="text-xs text-muted-foreground mt-1 font-mono">Tracking {claim.tracking_number}</div>}
                  {claim.tracking_number && claim.tracking_number.startsWith && !claim.tracking_number.startsWith("RM") && (
                    <a
                      href={`https://www.royalmail.com/track-your-item#/tracking-results/${claim.tracking_number}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-primary underline"
                      data-testid="public-track-link"
                    >
                      Track parcel <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {claim.pickup_url && (
                  <div className="bg-primary/5 border border-primary/30 rounded-2xl p-6" data-testid="public-pickup-card">
                    <div className="overline text-primary flex items-center gap-2"><Package className="w-3 h-3" /> Book pickup</div>
                    <div className="mt-3 text-sm">Royal Mail will collect your parcel from your door. Free — we reimburse the 30p Parcel Collect fee via your wallet.</div>
                    <div className="mt-3 flex gap-2">
                      <a
                        href={claim.pickup_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium py-2 px-3"
                        data-testid="public-pickup-btn"
                      >
                        Book pickup <ExternalLink className="w-3 h-3" />
                      </a>
                      <button
                        onClick={() => { navigator.clipboard.writeText(claim.pickup_url); toast.success("Link copied"); }}
                        className="rounded-lg border border-primary/30 py-2 px-3 hover:bg-primary/10"
                        data-testid="public-pickup-copy"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-10 text-xs text-muted-foreground text-center">
              This is a read-only public status page. Personal details are redacted for privacy.
            </div>
          </>
        )}
      </main>
    </div>
  );
}
